import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router';

import { ONLINE_GIVING_SETTINGS_PATH } from '@/lib/online-giving-settings';
import { hasPermission, isAdminOrOwner, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { cn } from '@/utils/cn';

interface SettingsTab {
  to: string;
  label: string;
  visible: boolean;
}

/** Church setup and administration — not personal profile or device enrolment. */
export const SettingsLayout = () => {
  const { t } = useTranslation('admin');
  const location = useLocation();
  const membership = useChurchStore((state) => state.membership);
  const currentMembership = useCurrentMembership();
  const isModuleEnabled = useChurchStore((state) => state.isModuleEnabled);

  const isPrivileged = isAdminOrOwner(membership);
  const adminModuleOn = isModuleEnabled('admin');
  const canViewSupportTickets = hasPermission(
    currentMembership,
    PERMISSIONS.SUPPORT_TICKETS_VIEW,
  );

  const tabs = useMemo<SettingsTab[]>(
    () =>
      [
        {
          to: '/settings/church',
          label: t('settings.tabs.church'),
          visible: isPrivileged,
        },
        {
          to: '/settings/venues',
          label: t('settings.tabs.venues'),
          visible: isPrivileged && adminModuleOn,
        },
        {
          to: '/settings/staff',
          label: t('settings.tabs.staff'),
          visible: isPrivileged && adminModuleOn,
        },
        {
          to: '/settings/modules',
          label: t('settings.tabs.modules'),
          visible: isPrivileged && adminModuleOn,
        },
        {
          to: ONLINE_GIVING_SETTINGS_PATH,
          label: t('settings.tabs.onlineGiving'),
          visible: isPrivileged && isModuleEnabled('payments'),
        },
        {
          to: '/settings/hubtel',
          label: t('settings.tabs.sms'),
          visible: isPrivileged && isModuleEnabled('messaging'),
        },
        {
          to: '/settings/security',
          label: t('settings.tabs.security'),
          visible: isPrivileged,
        },
        {
          to: '/settings/migration-assistant',
          label: t('settings.tabs.data'),
          visible: isPrivileged,
        },
        {
          to: '/support/tickets',
          label: t('settings.tabs.support'),
          visible: adminModuleOn && canViewSupportTickets,
        },
      ].filter((tab) => tab.visible),
    [adminModuleOn, canViewSupportTickets, isModuleEnabled, isPrivileged, t],
  );

  const firstTab = tabs[0]?.to ?? '/home';
  if (location.pathname === '/settings' || location.pathname === '/settings/') {
    return <Navigate to={firstTab} replace />;
  }

  return (
    <div>
      <div className="mb-6 flex gap-1 overflow-x-auto overflow-y-hidden border-b border-border">
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) =>
              cn(
                'relative -mb-px shrink-0 border-b-2 border-transparent px-3 py-2 text-body font-medium whitespace-nowrap',
                'text-fg-secondary transition-colors hover:text-fg',
                isActive && 'border-brand text-brand',
              )
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </div>

      <Outlet />
    </div>
  );
};
